/**
 * Service catalog — the products agents quote from. Zoho Products is the only source:
 * names, prices, units, Product Category, Sub Group and descriptions all come from there,
 * through n8n "[C] App 5 - Product Catalog" (REACT_APP_CATALOG_URL). Inactive products are left out.
 *
 * Refreshes with the appointments (app opens, app comes back, ↻ in the sync pill), so a change
 * in Zoho shows on the next refresh. The last list is kept on the phone for quoting with no signal.
 * Quotes already built keep the price they were built with.
 * Without Zoho (test mode) a small sample catalog is used.
 */
import { useSyncExternalStore } from 'react';
import { getAccessToken } from './auth';
import { LEADS_SOURCE } from './leads';

const CATALOG_URL = process.env.REACT_APP_CATALOG_URL;
export const CATALOG_SOURCE = LEADS_SOURCE === 'zoho' && CATALOG_URL ? 'zoho' : 'test';
const STORAGE_KEY = 'cie_catalog_v1';

// the order agents see — same as the service catalog sheet
export const CATEGORY_ORDER = ['Insulation', 'Air Sealing', 'Health & Remediation', 'Bathroom Venting', 'Ventilation', 'Others', 'Enbridge Authorized Inspector', 'HVAC', 'Warranty'];
const SUB_GROUP_ORDER = ['Top-Up', 'Removal', 'Spray Foam', 'Army Crawl', 'Basic Treatment', 'Full Mould Treatment Package'];

// unit → what the quote line shows
export const UNIT_LABEL = { sqft: 'SQFT', each: 'EA', flat: 'FLAT' };

const SAMPLE = [
  { id: 's1', name: 'Blown Cellulose — 0" to 18" ( Full Fill )', price: 3.2, unit: 'sqft', category: 'Insulation', subGroup: 'Top-Up' },
  { id: 's2', name: 'Blown-In (Fiberglass or Cellulose) — 1"–7" ( Removal )', price: 2.4, unit: 'sqft', category: 'Insulation', subGroup: 'Removal' },
  { id: 's3', name: 'Regular Air Sealing with Foam — per 500 sq ft', price: 200, unit: 'flat', category: 'Air Sealing', subGroup: '' },
  { id: 's4', name: 'Extra Baffles — per 300 sq ft', price: 30, unit: 'each', category: 'Air Sealing', subGroup: '' },
  { id: 's5', name: 'Hatch with Border — 2"', price: 360, unit: 'each', category: 'Air Sealing', subGroup: '' },
  { id: 's6', name: 'Bathroom Fan', price: 1200, unit: 'each', category: 'Bathroom Venting', subGroup: '' },
  { id: 's7', name: '10-Year Workmanship Warranty', price: 0, unit: 'flat', category: 'Warranty', subGroup: '', addByDefault: true },
].map((i) => ({ description: '', addByDefault: false, ...i }));

const readSaved = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return saved && Array.isArray(saved.items) ? saved : null;
  } catch {
    return null;
  }
};

const saved = CATALOG_SOURCE === 'zoho' ? readSaved() : null;
let state = {
  items: CATALOG_SOURCE === 'zoho' ? (saved ? saved.items : []) : SAMPLE,
  status: CATALOG_SOURCE === 'zoho' ? (saved ? 'ready' : 'idle') : 'ready',
  error: '',
  fetchedAt: saved ? saved.fetchedAt : null,
};
const listeners = new Set();
const set = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn());
};
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

let inFlight = null;
export const refreshCatalog = () => {
  if (CATALOG_SOURCE !== 'zoho') return Promise.resolve(state.items);
  if (inFlight) return inFlight;
  set({ status: state.items.length ? 'ready' : 'loading', error: '' });
  inFlight = (async () => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Sign in again to load the service catalog.');
      const res = await fetch(CATALOG_URL, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: '{}' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `Couldn’t load the service catalog (${res.status}).`);
      set({ items: body.items, status: 'ready', error: '', fetchedAt: body.fetchedAt });
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: body.items, fetchedAt: body.fetchedAt }));
      } catch {
        /* storage full — the list still works for this session */
      }
    } catch (err) {
      // keep the last list we had; only say "error" when there's nothing to quote from
      set({ status: state.items.length ? 'ready' : 'error', error: err.message || 'Couldn’t load the service catalog.' });
    } finally {
      inFlight = null;
    }
    return state.items;
  })();
  return inFlight;
};

export const useCatalog = () => useSyncExternalStore(subscribe, () => state);
export const getCatalog = () => state;

/* Lines every new quote package starts with — products ticked "Add to New Quotes" in Zoho
   (the 10-year warranty). Agents remove them like any other line, e.g. when a customer doesn't qualify. */
export const defaultLines = (items = state.items, atticSize) => items.filter((i) => i.addByDefault).map((i) => lineFromItem(i, atticSize));

// $0 lines (the warranty) read "Included" instead of a price
export const lineAmountLabel = (amount) => (Number(amount) ? `$${Number(amount).toFixed(2)}` : 'Included');

const rank = (list, v) => {
  const i = list.indexOf(v);
  return i === -1 ? list.length : i;
};

/**
 * Category → sub-group → items, in the sheet's order, filtered by a search.
 * Returns [{ category, groups: [{ subGroup, items }] }].
 */
export const groupCatalog = (items, search = '') => {
  const q = search.trim().toLowerCase();
  const hits = q ? items.filter((i) => `${i.name} ${i.category} ${i.subGroup}`.toLowerCase().includes(q)) : items;
  const cats = new Map();
  hits.forEach((i) => {
    if (!cats.has(i.category)) cats.set(i.category, new Map());
    const subs = cats.get(i.category);
    const key = i.subGroup || '';
    if (!subs.has(key)) subs.set(key, []);
    subs.get(key).push(i);
  });
  return [...cats.entries()]
    .sort(([a], [b]) => rank(CATEGORY_ORDER, a) - rank(CATEGORY_ORDER, b) || a.localeCompare(b))
    .map(([category, subs]) => ({
      category,
      groups: [...subs.entries()]
        // no sub-group first, then the sheet's order, then A–Z (HVAC brands)
        .sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : rank(SUB_GROUP_ORDER, a) - rank(SUB_GROUP_ORDER, b) || a.localeCompare(b)))
        .map(([subGroup, list]) => ({ subGroup, items: list })),
    }));
};

// "$3.20 / sq ft", "$360 each", "$200 flat"
export const priceLabel = (item) => {
  if (!Number(item.price)) return 'Included';
  const p = `$${Number(item.price).toLocaleString('en-CA', { minimumFractionDigits: item.price % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
  return item.unit === 'sqft' ? `${p} / sq ft` : item.unit === 'flat' ? `${p} flat` : `${p} each`;
};

/* "1,200", "1200 sqft", "500+700" (the calculator's parts) → 1200. 0 when there's nothing usable. */
export const parseSqft = (value) =>
  String(value == null ? '' : value)
    .replace(/,/g, '')
    .split('+')
    .reduce((sum, part) => sum + (parseFloat(part.replace(/[^\d.]/g, '')) || 0), 0);

/* Every attic area added up — the whole attic is what gets quoted unless a package is a "second attic". */
export const totalAtticSqft = (areas) => (areas || []).reduce((sum, a) => sum + parseSqft(a && a.atticSize), 0);

/* A quote line from a catalog item. Per-sq-ft items start at the package's attic size. */
export const lineFromItem = (item, atticSize) => {
  const sqft = parseSqft(atticSize);
  return {
    productId: item.id,
    name: item.name,
    category: item.category,
    subGroup: item.subGroup || '',
    description: item.description || '', // the customer text on the quote
    unit: UNIT_LABEL[item.unit] || 'EA',
    unitRate: Number(item.price) || 0,
    quantity: item.unit === 'sqft' && sqft > 0 ? sqft : 1,
  };
};
